import React, { useState, useEffect } from "react";
import { HiMenu } from "react-icons/hi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "../components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "./ui/button";
import { Label } from "../components/ui/label";
import Modal from "../components/Modal";
import { membershipService } from "../services/membershipService";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import {
  GoBriefcase,
  GoEye,
  GoFileSymlinkFile,
  GoKey,
  GoPerson,
} from "react-icons/go";
import useUserStore2 from "../stores/userStore2";
import useAppModeStore from "../stores/useAppModeStore";
import { QRCodeCanvas } from "qrcode.react";
import { LucideUserRoundPen, UserIcon } from "lucide-react";
import { LuScanQrCode } from "react-icons/lu";
import userService from "../services/userService";
import { GrCopy } from "react-icons/gr";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select.tsx";
import useUserStore from "../stores/userStore.js";
import { clear } from "idb-keyval";

const TopNavBar = ({ toggleMobileSidebar, communityName }) => {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  // Visibility states for password fields
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get all user data from useUserStore2
  const { user, setCurrentProfile, logout } = useUserStore2();
  const profilePicture = user?.profilePicture;

  // Get current profile from user object
  const currentProfile = user?.currentProfile;
  const profileId = currentProfile?.id || "No Profile ID";
  const userName = user?.name || "Loading...";

  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const { mode, toggleMode, setMode } = useAppModeStore();
  const isModeSwitchAllowed = [
    "POC Attendant",
    "Collector",
    "Super Admin",
  ].includes(user?.role);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError("New password and confirm password must match.");
      return;
    }

    setError(""); // Clear error if passwords match

    try {
      setLoading(true); // Show loading state

      const response = await userService.resetPassword({
        EntityId: user?.id, // Extract from useUserStore2
        OldPassword: currentPassword,
        NewPassword: newPassword,
      });

      toast.success("🎉 Password Changed successfully!", {
        autoClose: 2000,
      });
      console.log("Response:", response);
      setShowChangePasswordModal(false);
    } catch (error) {
      setError("Failed to reset password. Please try again.");
      console.error("Password reset error:", error);
    } finally {
      setLoading(false); // Hide loading state
    }
  };

  const routeNames = {
    // General
    login: "Login",
    dashboard: "Dashboard",
    "comm-dashboard": "Community Dashboard",
    settings: "Settings",

    // Community Management
    communities: "Communities",
    add: "Add Community",
    edit: "Edit Community",
    view: "View Community",
    "pending-requests": "Join Requests",
    "comm-pending": "Join Requests",
    ca: "Community Admin",

    // User Management
    users: "Users",
    "user-management": "User Management",
    "pending-requests": "Approval Requests", // Matches both user and community requests

    // POC Management
    poc: "POC Management",
    pocattendant: "POC Check-In",
    poccollector: "POC Check-In",

    // Material Management
    "material-management": "Resources",

    // Member/Group Management
    "member-management": "Members",
    "group-management": "Groups",
    "group-admin": "Group Approvals",

    // Specific sub-routes
    "new-community": "New Community", // For /communities/add
    "comm-view-request": "Request Details",
    "view-request": "Request Details",
    information: "QR Code Scanner",

    // Group Management sub-routes
    view: "Details",
    edit: "Edit",
    add: "Send invitation",

    // POC sub-routes
    "poc-management": "Service Points",

    // Additional mappings
    "material-management": "Material Management",
    "member-management": "Member Management",
    news: "News",
    transactions: "Transactions",
    "member-dashboard": "My Dashboard",
    materials: "Materials",
    "my-transactions": "My Transactions",
    "my-requests": "My Requests",
    pending: "Pending Requests",
  };

  const handleLogout = async () => {
    useUserStore.getState().logout();
    localStorage.clear(); // Clear stored user data
    window.location.href = "/login"; // Redirect to login page
    logout(); // Use the logout function from useUserStore2
    await clear();
  };

  const handleConfirmSwitch = async () => {
    const { setUser } = useUserStore2.getState();

    if (!currentProfile?.id) {
      alert("Please select a profile first.");
      return;
    }

    setLoading(true);

    console.log("user:", user);
    console.log("user?.currentProfile.id:", user?.currentProfile.id);
    console.log("currentProfile?.id:", currentProfile?.id);

    // Match user.profiles with selected profile and set as current
    const selectedProfile = user?.profiles?.find(
      (profile) => profile.id === user?.currentProfile.id
    );
    console.log("handleConfirmSwitch - selectedProfile:", selectedProfile);
    if (selectedProfile) {
      setCurrentProfile(selectedProfile);
    }

    setIsModalOpen(false);
    setLoading(false);

    // TODO: Review if it is really needed to fetch memberships again
    try {
      const membershipData = await membershipService.getMembershipById(
        user.id,
        currentProfile.id
      );

      const memberships = membershipData.map((membership) => ({
        membershipId: membership?._id || null,
        role: membership?.role || null,
        status: membership?.status || null,
        profileId: membership?.profile?._id || null,
        profileName: membership?.profile?.name || null,
        communityId: membership?.community?._id || null,
        communityName: membership?.community?.name || null,
        groupId: membership?.group?._id || null,
        groupName: membership?.group?.name || null,
        entityId: membership?.entity?._id || null,
        entityName: membership?.entity?.name || null,
        createDate: membership?.create_date || null,
        lastUpdate: membership?.last_update || null,
      }));
      // console.log("memberships:", memberships);

      if (memberships?.length > 0) {
        const membership = memberships[0];

        // Set currentProfile in useUserStore2
        // setCurrentProfile({
        //   id: membership.profileId,
        //   name: membership.profileName,
        //   communityId: membership.communityId,
        //   groupId: membership.groupId,
        //   role: membership.role,
        // });

        // Update Zustand user store with new memberships
        // setUser({
        //   ...user,
        //   memberships: membershipData.memberships,
        // });
      } else {
        console.warn("No membership data found for this profile.");
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error("Error fetching membership data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (path, index) => {
    const isViewAccountPage = pathnames[index - 1] === "view-account";
    const isEditAccountPage = pathnames[index - 1] === "edit-account";

    const isCommunityEditPage =
      pathnames[index - 1] === "edit" && pathnames[index - 2] === "communities";
    const isCommunityViewPage =
      pathnames[index - 1] === "view" && pathnames[index - 2] === "communities";

    // Handle community pages - show community name instead of ID
    if (
      (pathnames[index - 1] === "communities" ||
        pathnames[index - 1] === "member-management" ||
        pathnames[index - 1] === "group-management" ||
        pathnames[index - 1] === "comm-dashboard") &&
      index === pathnames.length - 1
    ) {
      return communityName || path;
    }

    // Handle account pages
    if (isViewAccountPage || isEditAccountPage) {
      if (index === pathnames.length - 1) {
        const action = isEditAccountPage ? "Editing" : "Viewing";
        return user ? (
          `${action}: ${user.name}`
        ) : (
          <Skeleton className="w-40 h-6" />
        );
      } else if (path === "edit-account") {
        return "Account";
      }
    }

    // Handle community edit/view pages
    if (
      (isCommunityEditPage || isCommunityViewPage) &&
      index === pathnames.length - 1
    ) {
      return communityName || path;
    }

    // Handle group-management/add case
    if (path === "add" && pathnames[index - 1] === "group-management") {
      return "Add Group";
    }

    // Handle member-management/add case
    if (path === "add" && pathnames[index - 1] === "member-management") {
      return "Add Member";
    }

    // Default case - check routeNames first, then fallback to formatted path
    return (
      routeNames[path] ||
      path
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  console.log("User: ", user);

  const shouldShowModeSwitch = () => {
    // Check if user has one of the allowed roles
    const hasAllowedRole = [
      "POC Attendant",
      "Super Admin",
      "Collector",
    ].includes(user?.role);

    // Check if user has profiles (only show if they have at least one profile)
    const hasProfiles = user?.profiles?.length > 0;

    return hasAllowedRole && hasProfiles;
  };

  return (
    <div className="flex items-center mt-9 px-8">
      <div className="flex items-center">
        <HiMenu
          className="text-2xl cursor-pointer md:hidden mr-2"
          onClick={toggleMobileSidebar}
        />
        <div className="flex flex-col">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to="/"
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {isSmallScreen && pathnames.length > 2 ? (
                <>
                  <BreadcrumbSeparator className="text-gray-500" />
                  <BreadcrumbItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="text-sm text-gray-500 hover:text-gray-700">
                        ...
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-white shadow-lg rounded-md p-2">
                        {pathnames.slice(1, -1).map((path, index) => {
                          const isNumber = !isNaN(path);
                          if (isNumber) return null;

                          // Special handling for account pages
                          if (
                            path === "edit-account" ||
                            path === "view-account"
                          ) {
                            return (
                              <DropdownMenuItem
                                key={path}
                                className="hover:bg-gray-100 rounded-md px-3 py-2"
                              >
                                <Link
                                  to={`/edit-account/${pathnames[index + 2]}`}
                                  className="text-sm text-gray-700 hover:text-gray-900"
                                >
                                  Account
                                </Link>
                              </DropdownMenuItem>
                            );
                          }

                          const routeTo = `/${pathnames
                            .slice(0, index + 2)
                            .join("/")}`;
                          return (
                            <DropdownMenuItem
                              key={path}
                              className="hover:bg-gray-100 rounded-md px-3 py-2"
                            >
                              <DropdownMenuItem
                                to={routeTo}
                                className="text-sm text-gray-700 hover:text-gray-900"
                              >
                                {getDisplayName(path, index)}
                              </DropdownMenuItem>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-gray-500" />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-sm font-medium text-gray-800">
                      {pathnames[pathnames.length - 2] === "edit-account"
                        ? `Editing: ${userName}`
                        : pathnames[pathnames.length - 2] === "view-account"
                        ? `Viewing: ${userName}`
                        : getDisplayName(
                            pathnames[pathnames.length - 1],
                            pathnames.length - 1
                          )}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              ) : (
                pathnames.map((path, index) => {
                  const isNumber = !isNaN(path);
                  if (isNumber) return null;

                  const isEditAccount = path === "edit-account";
                  const isViewAccount = path === "view-account";
                  const isAccountPage = isEditAccount || isViewAccount;
                  const isLast = index === pathnames.length - 1;

                  if (isAccountPage) {
                    return (
                      <React.Fragment key={path}>
                        <BreadcrumbSeparator className="text-gray-500" />
                        <BreadcrumbItem>
                          <BreadcrumbLink asChild>
                            <Link
                              to={`/view-account/${pathnames[index + 1]}`}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Account
                            </Link>
                          </BreadcrumbLink>
                        </BreadcrumbItem>
                        {isLast && (
                          <>
                            <BreadcrumbSeparator className="text-gray-500" />
                            <BreadcrumbItem>
                              <BreadcrumbPage className="text-sm font-medium text-gray-800">
                                {isEditAccount
                                  ? `Editing: ${userName}`
                                  : `Viewing: ${userName}`}
                              </BreadcrumbPage>
                            </BreadcrumbItem>
                          </>
                        )}
                      </React.Fragment>
                    );
                  }

                  const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;

                  return (
                    <React.Fragment key={path}>
                      <BreadcrumbSeparator className="text-gray-500" />
                      <BreadcrumbItem>
                        {isLast ? (
                          <BreadcrumbPage className="text-sm font-medium text-gray-800">
                            {getDisplayName(path, index)}
                          </BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link
                              to={routeTo}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              {getDisplayName(path, index)}
                            </Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  );
                })
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="flex items-center z-[39] space-x-6 ml-auto">
        {/* <img src={bellIcon} alt="Notification" className="h-9 w-9" /> */}

        {/* Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger>
            <div className="flex items-center space-x-3 cursor-pointer">
              <div className="hidden sm:block">
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-700 flex items-center space-x-1">
                    <span>{userName}</span>
                    <span className="text-xs font-semibold text-gray-400">
                      | {user?.role}
                    </span>
                  </div>
                  {/* Current Profile Name */}
                  <div className="text-xs border border-teal-500 text-teal-500 rounded-full bg-white p-1 px-2 inline-flex items-center space-x-1">
                    <GoPerson className="text-teal-500" />
                    <span className="">
                      {currentProfile?.name || "No Profile Selected"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt="User Profile"
                    className="h-full w-full rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = "";
                      e.target.className = "hidden"; // Hide the broken image
                    }}
                  />
                ) : (
                  <UserIcon className="h-6 w-6 text-gray-500" /> // Using Lucide-react icon
                )}
              </div>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="bg-white shadow-lg z-99 rounded-md p-2 w-50">
            <Link to={`/edit-account/${user?.id}`}>
              <DropdownMenuItem className="hover:bg-gray-100 px-3 py-2 flex items-center gap-2">
                <GoPerson className="w-4 h-4" />
                Account
              </DropdownMenuItem>
            </Link>

            <DropdownMenuItem
              className="hover:bg-gray-100 px-3 py-2 flex items-center gap-2"
              onClick={() => setIsModalOpen(true)}
            >
              <LucideUserRoundPen className="w-4 h-4" />
              Switch Profile
            </DropdownMenuItem>

            <Link to={`/my-transactions`}>
              <DropdownMenuItem className="hover:bg-gray-100 px-3 py-2 flex items-center gap-2">
                <GrCopy className="w-4 h-4" />
                My Transactions
              </DropdownMenuItem>
            </Link>

            <Link to={`/my-requests`}>
              <DropdownMenuItem className="hover:bg-gray-100 px-3 py-2 flex items-center gap-2">
                <GoFileSymlinkFile className="w-4 h-4" />
                My Requests
              </DropdownMenuItem>
            </Link>

            <DropdownMenuItem
              className="hover:bg-gray-100 px-3 py-2 flex items-center gap-2"
              onClick={() => setIsQrModalOpen(true)}
            >
              <LuScanQrCode className="w-4 h-4" />
              Show Profile QR
            </DropdownMenuItem>

            {/* <DropdownMenuItem
              className="hover:bg-gray-100 px-3 py-2 flex items-center gap-2"
              onClick={() => setShowChangePasswordModal(true)}
            >
              <GoKey className="w-4 h-4" />
              Change Password
            </DropdownMenuItem> */}

            <DropdownMenuItem
              onClick={handleLogout}
              className="hover:bg-red-100 px-3 py-2 text-red-600 flex items-center gap-2"
            >
              <GoEye className="w-4 h-4 text-red-600" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {shouldShowModeSwitch() && (
          <div className="flex items-center bg-white rounded-full overflow-hidden border border-gray-300">
            <button
              onClick={() => setMode("work")}
              className={`flex items-center gap-1 px-3 text-sm py-2 m-1 rounded-full transition-colors ${
                mode === "work"
                  ? "bg-teal-500 text-white"
                  : "bg-white text-gray-700"
              }`}
            >
              <GoBriefcase className="text-base" />
              <span className="hidden lg:inline">Work</span>
            </button>
            <button
              onClick={() => setMode("member")}
              className={`flex items-center gap-1 px-3 text-sm py-2 m-1 rounded-full transition-colors ${
                mode === "member"
                  ? "bg-teal-500 text-white"
                  : "bg-white text-gray-700"
              }`}
            >
              <GoPerson className="text-base" />
              <span className="hidden lg:inline">User</span>
            </button>
          </div>
        )}
      </div>

      {/* QR code modal */}
      <Modal isOpen={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Profile QR Code
          </h2>
        </div>

        {/* Profile Information */}
        <div className="space-y-3 text-sm border-t pt-4">
          {/* Profile ID */}
          <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
            <span className="text-gray-500 w-32">Profile ID</span>
            <span className="text-gray-900 font-medium">{profileId}</span>
          </div>

          {/* Date Created */}
          {/* <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
            <span className="text-gray-500 w-32">Date Created</span>
            <span className="text-gray-900 font-medium">
              {new Date(createdDate).toLocaleDateString()}
            </span>
          </div> */}
        </div>

        {/* Profile Name */}
        <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
          <span className="text-gray-500 w-32">Profile Name</span>
          <span className="text-gray-900 font-medium">
            {currentProfile?.name}
          </span>
        </div>

        <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
          <span className="text-gray-500 w-32">Profile QR</span>
          <span className="text-gray-900 font-medium">
            {/* QR Code */}
            {currentProfile?.name ? (
              <div className="flex justify-center mt-4">
                <QRCodeCanvas value={`${user?.id}/${profileId}`} size={200} />
              </div>
            ) : (
              <p className="text-center mt-4">No Profile Selected</p>
            )}
          </span>
        </div>

        {/* Buttons */}
        <div className="flex justify-between gap-3 mt-6">
          <Button
            variant="outline"
            className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
            onClick={() => setIsQrModalOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={() => setIsQrModalOpen(false)}>OK</Button>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        title="Change Password"
        description="Enter your new password below."
        icon={<GoKey className="text-green-500 " />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowChangePasswordModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={loading}>
              {loading ? "Saving..." : "Confirm"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-left">
          {" "}
          {/* Left-aligned input fields */}
          {/* Current Password */}
          <div className="flex flex-col">
            <Label>Current Password</Label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter Current Password"
                className="mt-1 block w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <FaEyeSlash className="text-gray-500" />
                ) : (
                  <FaEye className="text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {/* New Password */}
          <div className="flex flex-col">
            <Label>New Password</Label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter New Password"
                className="mt-1 block w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <FaEyeSlash className="text-gray-500" />
                ) : (
                  <FaEye className="text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {/* Confirm Password */}
          <div className="flex flex-col">
            <Label>Confirm Password</Label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm New Password"
                className="mt-1 block w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <FaEyeSlash className="text-gray-500" />
                ) : (
                  <FaEye className="text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {/* Error Message */}
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
      </Modal>

      {/* Modal for Switching Profile */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="p-2">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Switch Profile
          </h2>
          <hr className="mb-6 border-gray-200" />

          {/* Dropdown for selecting a profile */}
          <div>
            <Label
              htmlFor="profile-select"
              className="block text-left text-gray-700 font-medium"
            >
              Select Profile
            </Label>
            <Select
              value={currentProfile?.id || undefined} // Use undefined instead of empty string
              onValueChange={(selectedId) => {
                const selectedProfile = user?.profiles?.find(
                  (p) => p.id === selectedId
                );
                if (selectedProfile) {
                  setCurrentProfile({
                    id: selectedProfile.id,
                    name: selectedProfile.name,
                  });
                }
              }}
            >
              <SelectTrigger id="profile-select">
                <SelectValue placeholder="Select a profile" />
              </SelectTrigger>
              <SelectContent>
                {user?.profiles?.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <hr className="mt-6 mb-4 border-gray-200" />

          {/* Footer with Confirm and Cancel buttons */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSwitch} disabled={loading}>
              {loading ? "Switching..." : "Confirm"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TopNavBar;
